import React, { useEffect, useState, useCallback } from 'react';
import { timeAgo, fmtDate, fmtDateTime, parseUTC } from '../utils/dateUtils';
import { securityAuditApi } from '../api';
import { useAuth } from '../context/AuthContext';

const ACTION_META = {
  PASSWORD_RESET:       { icon: '🔐', color: '#E53E3E', label: 'Password Reset'    },
  EMPLOYEE_CREATED:     { icon: '➕', color: '#3182CE', label: 'Employee Created'   },
  EMPLOYEE_UPDATED:     { icon: '✏️', color: '#D69E2E', label: 'Employee Updated'   },
  ACCOUNT_DEACTIVATED:  { icon: '🚫', color: '#E53E3E', label: 'Account Deactivated'},
  ACCOUNT_ACTIVATED:    { icon: '✅', color: '#38A169', label: 'Account Activated'  },
  ROLE_CHANGED:         { icon: '🔄', color: '#805AD5', label: 'Role Changed'       },
};

export default function SecurityAuditLogPage() {
  const { user }                   = useAuth();
  const [data,    setData]         = useState({ items: [], total: 0, totalPages: 1 });
  const [loading, setLoading]      = useState(true);
  const [page,    setPage]         = useState(0);
  const [search,  setSearch]       = useState('');
  const isSuperAdmin               = user?.role === 'SUPER_ADMIN';

  const load = useCallback(() => {
    setLoading(true);
    securityAuditApi.getLogs({ page, size: 30 })
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const filtered = data.items.filter(log =>
    !search ||
    log.actionType?.toLowerCase().includes(search.toLowerCase()) ||
    log.performedBy?.toLowerCase().includes(search.toLowerCase()) ||
    log.targetEmployee?.toLowerCase().includes(search.toLowerCase()) ||
    log.details?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ padding: '20px', maxWidth: '900px' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--color-text)', marginBottom: '4px' }}>
          Security Audit Log
        </h1>
        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
          {isSuperAdmin
            ? `All sensitive user-management actions in your company — ${data.total} total entries`
            : 'Actions you have personally performed on other employees'}
        </p>
      </div>

      {/* Context box */}
      <div style={{
        padding: '12px 16px',
        background: isSuperAdmin ? '#FFF5F5' : '#EBF8FF',
        border: `1px solid ${isSuperAdmin ? '#FEB2B2' : '#BEE3F8'}`,
        borderRadius: 'var(--radius-md)', marginBottom: '16px', fontSize: '13px',
        color: isSuperAdmin ? '#C53030' : '#2B6CB0',
      }}>
        {isSuperAdmin ? (
          <>
            <strong>⚠️ Super Admin view:</strong> This log shows every password reset,
            role change, account activation/deactivation performed by any admin in your company.
            If you see an unexplained password reset, investigate immediately.
          </>
        ) : (
          <>
            <strong>ℹ️ Admin view:</strong> You can only see actions you personally performed.
            Your Super Admin can see the full log including all your actions.
          </>
        )}
      </div>

      {/* Search */}
      <input
        placeholder="Search by action, employee, or details…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{
          width: '100%', padding: '9px 14px', marginBottom: '12px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)',
          background: 'var(--color-surface)',
          fontSize: '14px', color: 'var(--color-text)',
        }}
      />

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-muted)' }}>
          Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-muted)' }}>
          <p style={{ fontSize: '28px', marginBottom: '10px' }}>🔐</p>
          <p>{search ? 'No entries match your search' : 'No security events recorded yet'}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {filtered.map(log => {
            const meta = ACTION_META[log.actionType] ||
              { icon: '•', color: 'var(--color-text-muted)', label: log.actionType };
            const isPasswordReset = log.actionType === 'PASSWORD_RESET';
            return (
              <div key={log.id} style={{
                display: 'flex', gap: '12px', padding: '12px 16px',
                background: isPasswordReset ? '#FFF5F5' : 'var(--color-surface)',
                borderRadius: 'var(--radius-md)',
                border: `1px solid ${isPasswordReset ? '#FEB2B2' : 'var(--color-border)'}`,
              }}>
                <span style={{ fontSize: '18px', flexShrink: 0, marginTop: '2px' }}>
                  {meta.icon}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '4px', alignItems: 'center' }}>
                    <span style={{
                      fontSize: '11px', fontWeight: 700, padding: '2px 8px',
                      borderRadius: '999px', background: `${meta.color}18`,
                      color: meta.color, border: `1px solid ${meta.color}44`,
                    }}>
                      {meta.label}
                    </span>
                    {isSuperAdmin && (
                      <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                        by <strong>{log.performedBy}</strong>
                        <span style={{ color: 'var(--color-text-muted)' }}> ({log.performedByRole})</span>
                      </span>
                    )}
                    {log.targetEmployee && (
                      <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                        → <strong>{log.targetEmployee}</strong>
                      </span>
                    )}
                  </div>
                  {log.details && (
                    <p style={{
                      fontSize: '12px', color: 'var(--color-text-muted)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {log.details}
                    </p>
                  )}
                  {log.ipAddress && (
                    <p style={{ fontSize: '11px', color: 'var(--color-border)', marginTop: '2px' }}>
                      IP: {log.ipAddress}
                    </p>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <p style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                    {timeAgo(log.timestamp)}
                  </p>
                  <p style={{ fontSize: '10px', color: 'var(--color-border)', marginTop: '2px' }}>
                    {fmtDateTime(log.timestamp)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {data.totalPages > 1 && (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '16px', alignItems: 'center' }}>
          <button onClick={() => setPage(p => Math.max(0, p-1))} disabled={page === 0}
            style={{ padding: '6px 14px', borderRadius: 'var(--radius-sm)', cursor: page === 0 ? 'not-allowed' : 'pointer', background: 'var(--color-bg)', border: '1px solid var(--color-border)', opacity: page === 0 ? 0.5 : 1 }}>
            ← Prev
          </button>
          <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
            Page {page + 1} of {data.totalPages}
          </span>
          <button onClick={() => setPage(p => Math.min(data.totalPages-1, p+1))} disabled={page >= data.totalPages-1}
            style={{ padding: '6px 14px', borderRadius: 'var(--radius-sm)', cursor: page >= data.totalPages-1 ? 'not-allowed' : 'pointer', background: 'var(--color-bg)', border: '1px solid var(--color-border)', opacity: page >= data.totalPages-1 ? 0.5 : 1 }}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
